import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import SwitchField from '../../components/Fields/SwitchField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import BusinessPartnerModel from '../../models/BusinessPartnerModel';
import DataService from '../../services/data.service';
import Button from './../../components/Buttons/Button';
import ButtonBar from './../../components/Fields/ButtonBar';
import CheckboxField from './../../components/Fields/CheckboxField';
import Form from './../../components/Fields/Form';
import SelectListField from './../../components/Fields/SelectListField';
import TextboxField from './../../components/Fields/TextboxField';
import Unauthorized from './../Authentication/Unauthorized';
import BusinessPartner_popup_CreateBusinessPartner from './Popups/BusinessPartner_popup_CreateBusinessPartner';

interface BusinessPartnerSearchCriteria {
    quickSearch: string
    businessPartnerTypeId?: number
    active: boolean
    rememberFilter: boolean,
}

const BusinessPartnerSearchView: React.FC = () => {
    const [data, setData] = useState<BusinessPartnerModel[]>([])
    const [showCreatePopup, setShowCreatePopup] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor } = useAuth();
    const navigate = useNavigate()
    const [formData, setFormData] = useState<BusinessPartnerSearchCriteria>({
        quickSearch: '',
        active: true,
        rememberFilter: false,
    });

    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes?active=true&applicationFunction=' + ApplicationFunctions.ViewBusinessPartner, '-- All types');

    useEffect(() => {
        const filterItem = localStorage.getItem("businessPartnerFilter");
        if (filterItem !== null) {
            let filter: BusinessPartnerSearchCriteria = JSON.parse(filterItem);
            setFormData(x => ({ ...filter }));
        }
    }, [])

    function search() {
        setShowSpinner(true);

        if (formData.rememberFilter)
            localStorage.setItem('businessPartnerFilter', JSON.stringify(formData));
        else
            localStorage.removeItem(`businessPartnerFilter`);

        DataService
            .post<BusinessPartnerModel[]>(`api/v1/BusinessPartners/Search/`, formData)
            .then(response => {
                setShowSpinner(false);
                setData(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function setDefaultCriteria() {
        let defaultCriteria = {
            quickSearch: '',
            active: true,
            rememberFilter: formData?.rememberFilter ?? false,
        }

        setFormData({ ...defaultCriteria })
    }


    function deleteRow(rowData) {
        DataService.delete('api/v1/BusinessPartners/', rowData.id)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);

        console.log(name + ': ' + value);
    }

    function hideCreatePopup(id?: number) {
        setShowCreatePopup(false)
        if (id) {
            navigate('/plm/BusinessPartners/Details/' + id)
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 2 },
            { name: 'businessPartnerTypeId', label: 'Type', labelSize: 2 },
            { name: 'active', label: 'Active business-partners only', labelSize: 2 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 2 },

        ],
        handleSubmit: search,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    function getContactInfo(row: BusinessPartnerModel) {
        return row.contactInfos.map(x => x.details).join(', ');
    }

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { dataProp: 'thumbnailUrl', dataType: 'bigimage', width: 75 },
        { title: 'Type', dataProp: 'businessPartnerType.name', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Name', dataProp: 'name', sort: 'asc', filterInfo: { filterType: 'Textbox' } },
        { title: 'Division', dataProp: 'division', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Contactpersons', dataProp: 'contactPersons', filterInfo: { filterType: 'Textbox' } },
        { title: 'Contact info', getCellData: getContactInfo, filterInfo: { filterType: 'Textbox' } },
        { title: 'Address', dataProp: 'address', filterInfo: { filterType: 'Textbox' } },
        { title: 'Coordination', dataProp: 'coordination', filterInfo: { filterType: 'Textbox' } },
        { title: 'Begin', dataProp: 'beginDate', dataType: 'date' },
        { title: 'End', dataProp: 'endDate', dataType: 'date' },
    ];
    if (isAuthorizedFor(ApplicationFunctions.CreateBusinessPartner) || isAuthorizedFor(ApplicationFunctions.DeleteBusinessPartner)) {
        colInfos.push(
            {
                headerButtons: [{ text: "Add", icon: 'fa-plus-square', color: '#26A69A', onClick: () => { setShowCreatePopup(true) }, applicationFunctions: [ApplicationFunctions.CreateBusinessPartner] }],
                rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow, applicationFunctions: [ApplicationFunctions.DeleteBusinessPartner] }],
            });
    }

    if (!isAuthorizedFor(ApplicationFunctions.ViewBusinessPartner)) {
        return (<Unauthorized />);
    }

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem icon="fa fa-plus" iconVariant="success" title="Create new business-partner" width={260} onClick={() => { setShowCreatePopup(true); }} appFunction={ApplicationFunctions.CreateBusinessPartner} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Business-partners' actions={formActions} footer={(<>
                    <ButtonBar offset={2}>
                        <Button text='Search' {...getSubmitButtonProps()} />
                        <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                    </ButtonBar>
                </>)}>
                <Container fluid>
                    <TextboxField {...getFieldProps('quickSearch')} />
                    <SelectListField {...getFieldProps('businessPartnerTypeId')} options={businessPartnerTypes} />
                    <SwitchField {...getFieldProps('active')} />
                    <SwitchField {...getFieldProps('rememberFilter')} />
                </Container>
            </Form>
            <div className="card" style={{ margin: 12 }}>
                <DataTable
                    name='dtBusinessPartners'
                    colInfos={colInfos}
                    tableData={data}
                    navigateTitle='Business-partner'
                    navigateUrl='/plm/BusinessPartners/Details/'
                    pageSize={50}
                    />
            </div>
            {showCreatePopup && <BusinessPartner_popup_CreateBusinessPartner show={showCreatePopup} onHide={hideCreatePopup} />}
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default BusinessPartnerSearchView;